import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Logo } from '../components/common/Logo';
import { Button } from '../components/ui/button';
import { api, getApiURL } from '@/lib/axios';
import { MemoryVideoPlayer } from '../components/MemoryVideoPlayer';
import { GoogleReviewPrompt } from '../components/GoogleReviewPrompt';
import { memoryService } from '@/services/memoryService';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useTranslation } from '@/i18n';

interface Memory {
    id: string;
    slug: string;
    status: 'draft' | 'processing' | 'done' | 'failed';
    videoUrl: string | null;
}

interface Restaurant {
    name: string;
    logoUrl: string;
    google_review_url: string | null;
}

export function MemoryPage() {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const { trackMemory, trackVideo, trackShare, trackDownload, trackReview } = useAnalytics();
    const { t } = useTranslation();

    // State
    const [memory, setMemory] = useState<Memory | null>(null);
    const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Share state
    const [isSharing, setIsSharing] = useState(false);
    const [canShare, setCanShare] = useState(false);

    // Logo color state (adaptive based on video luminosity)
    const [logoColor, setLogoColor] = useState<'white' | 'black'>('white');

    const handleReviewClick = useCallback(() => {
        if (!slug) return;
        trackReview.submit(slug);
        fetch(`${getApiURL()}/v1/memories/${slug}/review_click`, {
            method: 'POST',
            keepalive: true,
        }).catch(() => { /* silent fail — tracking is best-effort */ });
    }, [slug, trackReview]);

    const [showPrompt, setShowPrompt] = useState(false);
    const [videoEnded, setVideoEnded] = useState(false);

    // Track video_ready only once
    const hasTrackedVideoReady = useRef(false);

     // Load memory data on mount
    useEffect(() => {
        async function loadMemory() {
            if (!slug) {
                navigate('/', { replace: true });
                return;
            }

            try {
                setIsLoading(true);
                const response = await api.get(`/v1/memories/${slug}`);
                const memoryData = response.data.memory;
                const restaurantData = response.data.restaurant;
                
                setMemory(memoryData);
                setRestaurant(restaurantData);

                // Track: memory page viewed
                trackMemory.view(slug, memoryData.status, restaurantData?.name);

                // Track: video ready (if already available)
                if (memoryData.videoUrl && !hasTrackedVideoReady.current) {
                    trackVideo.ready(slug, restaurantData?.name);
                    hasTrackedVideoReady.current = true;
                }
            } catch (error) {
                console.error('Error loading memory:', error);
                const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
                trackMemory.error(slug, errorMessage);
                navigate('/', { replace: true });
            } finally {
                setIsLoading(false);
            }
        }

        loadMemory();
    }, [slug, navigate, trackMemory, trackVideo]);

    useEffect(() => {
        if (videoEnded && restaurant?.google_review_url) {
            setShowPrompt(true);
        }
    }, [videoEnded, restaurant?.google_review_url]);

    // Detect Web Share API file support
    useEffect(() => {
        const checkShareSupport = async () => {
            if (!navigator.share || !navigator.canShare) {
                setCanShare(false);
                return;
            }
            // Create a test file to check if file sharing is supported
            const testFile = new File(['test'], 'test.mp4', { type: 'video/mp4' });
            try {
                const supported = navigator.canShare({ files: [testFile] });
                setCanShare(supported);
            } catch {
                setCanShare(false);
            }
        };
        checkShareSupport();
    }, []);

    // Helper to fetch video as File with descriptive name
    const fetchVideoAsFile = async (url: string): Promise<File> => {
        const response = await fetch(url, { mode: 'cors', cache: 'no-store' });
        const blob = await response.blob();
        const restaurantName = restaurant?.name
            ?.replace(/[^a-zA-Z0-9áéíóúñÁÉÍÓÚÑ\s]/g, '')
            .replace(/\s+/g, '-')
            .substring(0, 30) || t('theRestaurant');
        const fileName = `${t('myMemoryAt', { name: restaurantName }).replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '-')}.mp4`;
        return new File([blob], fileName, { type: 'video/mp4' });
    };

    // Share video using Web Share API
    const handleShareVideo = async () => {
        if (!memory?.videoUrl || !slug) return;

        // Track: share button clicked
        trackShare.click(slug, 'native');

        try {
            setIsSharing(true);
            const file = await fetchVideoAsFile(memory.videoUrl);

            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({
                    files: [file],
                    title: t('myMemoryAt', { name: restaurant?.name || t('theRestaurant') }),
                });
                // Track: successful file share
                trackShare.success(slug, 'file');
            } else {
                await navigator.share({
                    title: t('myMemoryAt', { name: restaurant?.name || t('theRestaurant') }),
                    url: window.location.href,
                });
                // Track: successful link share (fallback)
                trackShare.success(slug, 'link');
            }
        } catch (err) {
            // User cancelled share or error occurred
            if ((err as Error).name !== 'AbortError') {
                console.error('Error sharing video file, falling back to link:', err);
                try {
                    await navigator.share({
                        title: t('myMemoryAt', { name: restaurant?.name || t('theRestaurant') }),
                        url: window.location.href,
                    });
                    trackShare.success(slug, 'link');
                } catch (linkErr) {
                    console.error('Error sharing link:', linkErr);
                    const errorMessage = linkErr instanceof Error ? linkErr.message : 'Error desconocido';
                    trackShare.error(slug, errorMessage);
                }
            }
            // AbortError means user cancelled, not an error
        } finally {
            setIsSharing(false);
        }
    };



    const handleDownloadVideo = async () => {
        if (!memory?.videoUrl || !slug) return;

        // Track: download button clicked
        trackDownload.click(slug);

        try {
            const response = await fetch(memory.videoUrl, { mode: 'cors', cache: 'no-store' });
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `kombu-recuerdo-${slug}.mp4`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            // Track: successful download
            trackDownload.success(slug);
        } catch (err) {
            console.error('Error downloading video:', err);
            const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
            trackDownload.error(slug, errorMessage);
            alert(t('downloadError'));
        }
    };

    const handleDismissPrompt = () => {
        setShowPrompt(false);
    };

    // Show loading state while fetching data
    if (isLoading || !memory || !restaurant) {
        return (
            <div className="min-h-screen bg-background-primary flex items-center justify-center">
                <div className="text-center space-y-2">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-content-primary mx-auto"></div>
                    <p className="font-serif text-sm text-content-secondary">{t('loading')}</p>
                </div>
            </div>
        );
    }

    // Video is ready - show fullscreen immersive experience
    if (memory.videoUrl && restaurant.logoUrl) {
        return (
            <>
                {/* Fullscreen Video Player with Branding Overlay */}
                <MemoryVideoPlayer
                    videoUrl={memory.videoUrl}
                    memorySlug={slug}
                    onLogoColorChange={setLogoColor}
                    onVideoEnd={() => setVideoEnded(true)}
                />

                {showPrompt && restaurant.google_review_url && (
                    <GoogleReviewPrompt
                        googleReviewUrl={restaurant.google_review_url}
                        onReviewClick={handleReviewClick}
                        onDismiss={handleDismissPrompt}
                    />
                )}

                {/* Share Section - Fixed at bottom with glassmorphism, centered on desktop */}
                <div className="fixed bottom-0 left-0 right-0 z-20">
                    <div className="max-w-md mx-auto">
                        {/* Glassmorphism Container */}
                    <div 
                        className="rounded-t-2xl p-4 space-y-3"
                        style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            backdropFilter: 'blur(10px)',
                            WebkitBackdropFilter: 'blur(10px)', // Safari support
                        }}
                    >
                        {/* Share Button */}
                        <Button
                            className="w-full h-11 font-sans text-sm font-medium uppercase tracking-wide"
                            style={{
                                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                                color: '#fff',
                            }}
                            onClick={canShare ? handleShareVideo : handleDownloadVideo}
                            disabled={isSharing}
                        >
                            {isSharing ? (
                                <span className="flex items-center justify-center gap-2">
                                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                                    {t('preparing')}
                                </span>
                            ) : (
                                t('shareMemory')
                            )}
                        </Button>

                        {/* Collaboration Text - Adaptive color based on video luminosity */}
                        <p 
                            className="text-xs text-center font-sans"
                            style={{
                                color: logoColor === 'white' ? '#fff' : '#000',
                                textShadow: logoColor === 'white'
                                    ? '0 2px 8px rgba(0, 0, 0, 0.8)'
                                    : '0 2px 8px rgba(255, 255, 255, 0.8)'
                            }}
                        >
                            {t('collaboration')}
                        </p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // Video not ready - show standard page layout
    return (
        <div className="min-h-screen bg-background-primary">
            {/* Top Navigation */}
            <div className="sticky top-0 z-10 backdrop-blur-sm bg-white/80 h-[48px] flex items-center justify-center px-4">
                <Logo
                    src={restaurant.logoUrl}
                    alt={restaurant.name}
                    className="h-6"
                />
            </div>

            {/* Main Content - centered on desktop */}
            <div className="max-w-md mx-auto px-4 py-6 space-y-8">
                {/* Estado: Video no disponible aún */}
                {!memory.videoUrl && (
                    <div className="space-y-6 text-center">
                        <h1 className="font-serif text-xl text-content-primary font-light">
                            {t('thanksVisit', { name: restaurant.name })}
                        </h1>

                        <p className="font-serif text-base text-content-secondary font-light">
                            {t('memorySoon')}
                        </p>
                    </div>
                )}

                {/* Estado: Error */}
                {memory.status === 'failed' && (
                    <div className="space-y-6 text-center">
                        <h1 className="font-serif text-xl text-content-primary font-light">
                            {t('thanksVisit', { name: restaurant.name })}
                        </h1>

                        <p className="font-serif text-base text-content-secondary font-light">
                            {t('memoryErrorStatus')}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
